import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Calendar, Edit2, Plus, Trash2,
  TrendingUp, TrendingDown, DollarSign, AlertCircle,
  Camera, Clock, CheckCircle, ExternalLink, ChevronRight, BarChart3,
} from 'lucide-react';
import { PageHeader, type ActionDef } from '../../components/ui/PageHeader';
import { toast } from 'sonner';
import { apiGet, apiPatch, apiPost, apiDelete } from '../../api/client';
import type {
  Project, ProjectFinanceSummary, ProjectExpense, ProjectRevenue,
  ProjectCollaboratorAssignment, ProjectActivity, Collaborator,
  ProjectStatus, ProjectPriority, ExpenseCategory, PaymentMethod,
  ProjectCollaboratorRole, ExternalPhotographer, Client,
} from '../../api/types';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal, ConfirmDialog } from '../../components/ui/Modal';
import { Skeleton } from '../../components/ui/Skeleton';
import { FinancialReportModal } from '../../components/FinancialReportModal';
import { Badge } from '../../components/ui/Badge';
import { UserAvatar } from '../../components/ui/UserAvatar';
import { DataTable, type ColumnDef } from '../../components/ui/DataTable';
import { formatCurrency, formatDate } from '../../utils/format';
import { usePermissions } from '../../hooks/usePermission';
import NotesSection from './NotesSection';

// ─── Label maps ───────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<ProjectStatus, string> = {
  BOZZA: 'Bozza', PREVENTIVATO: 'Preventivato', CONFERMATO: 'Confermato',
  IN_CORSO: 'In corso', IN_ATTESA: 'In attesa', COMPLETATO: 'Completato',
  FATTURATO: 'Fatturato', CHIUSO: 'Chiuso', ANNULLATO: 'Annullato',
};
const STATUS_COLORS: Record<ProjectStatus, string> = {
  BOZZA: 'var(--color-text-faint)', PREVENTIVATO: 'var(--color-text-muted)',
  CONFERMATO: 'var(--color-success)', IN_CORSO: 'var(--color-primary)',
  IN_ATTESA: 'var(--color-warning)', COMPLETATO: 'var(--color-gold)',
  FATTURATO: 'var(--color-success)', CHIUSO: 'var(--color-text-faint)', ANNULLATO: 'var(--color-error)',
};
const STATUS_SOLID: Record<string, string> = {
  'var(--color-text-faint)': '#6B7280',
  'var(--color-text-muted)': '#9CA3AF',
  'var(--color-success)': '#22C55E',
  'var(--color-primary)': '#3E91FF',
  'var(--color-warning)': '#F59E0B',
  'var(--color-gold)': '#D97706',
  'var(--color-error)': '#EF4444',
};
const PRIORITY_LABELS: Record<ProjectPriority, string> = { BASSA: 'Bassa', MEDIA: 'Media', ALTA: 'Alta', URGENTE: 'Urgente' };
const PRIORITY_COLORS: Record<ProjectPriority, string> = {
  URGENTE: 'var(--color-error)', ALTA: 'var(--color-warning)',
  MEDIA: 'var(--color-primary)', BASSA: 'var(--color-text-faint)',
};
const TYPE_LABELS: Record<string, string> = {
  INTERNO: 'Interno', EVENTO_CORRELATO: 'Evento correlato',
  POST_PRODUZIONE_ESTERNA: 'Post-produzione esterna', ALTRO: 'Altro',
};
const EXT_JOB_LABELS: Record<string, string> = {
  SELEZIONE: 'Selezione', CULLING: 'Culling', EDITING_BASE: 'Editing base',
  EDITING_AVANZATO: 'Editing avanzato', IMPAGINAZIONE_ALBUM: 'Impaginazione album',
  EXPORT_GALLERIA: 'Export galleria', WORKFLOW_COMPLETO: 'Workflow completo', ALTRO: 'Altro',
};
const EXPENSE_CATEGORIES: ExpenseCategory[] = [
  'TRASPORTI', 'ALLOGGIO', 'PASTI', 'ATTREZZATURA', 'NOLEGGI',
  'COLLABORATORI', 'POST_PRODUZIONE', 'STAMPE', 'ALBUM', 'SOFTWARE', 'MARKETING', 'VARIE',
];
const EXPENSE_CAT_LABELS: Record<ExpenseCategory, string> = {
  TRASPORTI: 'Trasporti', ALLOGGIO: 'Alloggio', PASTI: 'Pasti', ATTREZZATURA: 'Attrezzatura',
  NOLEGGI: 'Noleggi', COLLABORATORI: 'Collaboratori', POST_PRODUZIONE: 'Post-produzione',
  STAMPE: 'Stampe', ALBUM: 'Album', SOFTWARE: 'Software', MARKETING: 'Marketing', VARIE: 'Varie',
};
const PAYMENT_METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'CONTANTI', label: 'Contanti' }, { value: 'BONIFICO', label: 'Bonifico' },
  { value: 'CARTA', label: 'Carta' }, { value: 'PAYPAL', label: 'PayPal' }, { value: 'ALTRO', label: 'Altro' },
];
const COLLAB_ROLE_LABELS: Record<ProjectCollaboratorRole, string> = {
  EDITOR: 'Editor', CULLER: 'Culler', ALBUM_DESIGNER: 'Album designer',
  REVIEWER: 'Reviewer', PROJECT_SUPPORT: 'Supporto progetto',
};
const NEXT_STATUSES: Record<ProjectStatus, ProjectStatus[]> = {
  BOZZA: ['PREVENTIVATO', 'ANNULLATO'],
  PREVENTIVATO: ['CONFERMATO', 'ANNULLATO'],
  CONFERMATO: ['IN_CORSO', 'IN_ATTESA', 'ANNULLATO'],
  IN_CORSO: ['COMPLETATO', 'IN_ATTESA'],
  IN_ATTESA: ['IN_CORSO', 'COMPLETATO'],
  COMPLETATO: ['FATTURATO', 'CHIUSO'],
  FATTURATO: ['CHIUSO'],
  CHIUSO: [],
  ANNULLATO: [],
};

// ─── Shared sub-components ───────────────────────────────────────────────────

function SectionCard({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value?: string | null | React.ReactNode }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: '0.5rem', padding: '0.375rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', width: 160, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{value}</span>
    </div>
  );
}

function KpiMini({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div style={{ background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', padding: '0.875rem', flex: 1, minWidth: 120 }}>
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 }}>{label}</p>
      <p style={{ fontSize: 'var(--text-md)', fontWeight: 700, color: color ?? 'var(--color-text)' }}>{value}</p>
      {sub && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.125rem' }}>{sub}</p>}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { hasPermission } = usePermissions();
  const canFinancial = hasPermission('projects.financial');
  const [activeTab, setActiveTab] = useState<string>('overview');
  const safeTab = (activeTab === 'economy' && !canFinancial) ? 'overview' : activeTab;
  const [showReport, setShowReport] = useState(false);

  const { data: project, isLoading } = useQuery<Project>({
    queryKey: ['project', id],
    queryFn: () => apiGet(`/projects/${id}`),
  });
  const { data: finance } = useQuery<ProjectFinanceSummary>({
    queryKey: ['project-finance', id],
    queryFn: () => apiGet(`/projects/${id}/finance-summary`),
    enabled: safeTab === 'economy' && canFinancial,
  });

  type ReportShape = ProjectFinanceSummary & {
    roi: number; estimatedHours: number; hourlyRate: number;
    breakEvenRevenue: number; breakEvenPct: number; breakEvenReached: boolean;
    profitPerGuest?: number; daysUntilEvent?: number;
  };
  const { data: reportData, isFetching: reportLoading } = useQuery<ReportShape>({
    queryKey: ['project-report', id],
    queryFn: () => apiGet(`/projects/${id}/financial-report`),
    enabled: showReport,
  });

  const statusMutation = useMutation({
    mutationFn: (status: ProjectStatus) => apiPatch(`/projects/${id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project', id] }); qc.invalidateQueries({ queryKey: ['projects'] }); toast.success('Stato aggiornato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Skeleton height={60} /><Skeleton height={200} /><Skeleton height={300} />
    </div>
  );
  if (!project) return <div style={{ color: 'var(--color-error)', padding: '2rem' }}>Progetto non trovato</div>;

  const nextStatuses = NEXT_STATUSES[project.status] ?? [];
  const isExternal = project.type === 'POST_PRODUZIONE_ESTERNA';

  const tabs = ([
    { key: 'overview', label: 'Panoramica' },
    ...(canFinancial ? [{ key: 'economy', label: 'Economia' }] : []),
    { key: 'collaborators', label: 'Collaboratori' },
    { key: 'activity', label: 'Attività' },
  ] as const);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <style>{`
        .project-overview-grid { display: grid; grid-template-columns: 1fr; gap: 1rem; }
        .project-overview-grid.external { grid-template-columns: 1fr 1fr; }
        @media (max-width: 767px) {
          .project-overview-grid.external { grid-template-columns: 1fr; }
        }
      `}</style>
      <PageHeader
        backTo="/projects"
        title={project.title}
        badge={
          <>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontFamily: 'monospace', background: 'var(--glass-bg-card)', padding: '0.125rem 0.5rem', borderRadius: 'var(--radius-interactive)' }}>{project.code}</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 12px', borderRadius: 'var(--radius-interactive)', fontSize: 12, fontWeight: 600, color: '#FFFFFF', background: STATUS_SOLID[STATUS_COLORS[project.status]] ?? '#6B7280' }}>
              {STATUS_LABELS[project.status]}
            </span>
            <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: PRIORITY_COLORS[project.priority] }}>{PRIORITY_LABELS[project.priority]}</span>
          </>
        }
        metadata={
          project.deadline ? (
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <Calendar size={14} /> Scadenza: {formatDate(project.deadline)}
            </span>
          ) : undefined
        }
      >
        {nextStatuses.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, flexWrap: 'wrap' }}>
            {nextStatuses.map(s => (
              <Button key={s} size="sm" variant={s === 'ANNULLATO' ? 'danger' : 'secondary'}
                onClick={() => statusMutation.mutate(s)} loading={statusMutation.isPending}>
                → {STATUS_LABELS[s]}
              </Button>
            ))}
          </div>
        )}
      </PageHeader>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.375rem', paddingBottom: '0.5rem' }}>
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.5rem 1rem',
              background: safeTab === tab.key ? 'var(--color-primary-highlight)' : 'transparent',
              border: safeTab === tab.key ? '1px solid rgba(79,152,163,0.3)' : '1px solid transparent',
              borderRadius: 'var(--radius-interactive)',
              cursor: 'pointer',
              color: safeTab === tab.key ? 'var(--color-primary)' : 'var(--color-text-muted)',
              fontSize: 'var(--text-sm)',
              fontWeight: safeTab === tab.key ? 600 : 400,
              transition: 'all var(--spring-duration) var(--spring-easing)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {safeTab === 'overview' && <OverviewTab project={project} isExternal={isExternal} />}
      {safeTab === 'economy' && canFinancial && <EconomyTab project={project} finance={finance} id={id!} />}
      {safeTab === 'collaborators' && <CollaboratorsTab project={project} id={id!} />}
      {safeTab === 'activity' && <ActivityTab activities={project.activities ?? []} id={id!} />}
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({ project, isExternal }: { project: Project; isExternal: boolean }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: project.title,
    description: project.description ?? '',
    deadline: project.deadline ? project.deadline.split('T')[0] : '',
    startDate: project.startDate ? project.startDate.split('T')[0] : '',
    deliveryDeadline: project.deliveryDeadline ? project.deliveryDeadline.split('T')[0] : '',
    photosToEditCount: String(project.photosToEditCount ?? ''),
    photosDeliveredCount: String(project.photosDeliveredCount ?? ''),
    editingStyleNotes: project.editingStyleNotes ?? '',
    deliveryMethod: project.deliveryMethod ?? '',
    externalSalePrice: String(project.externalSalePrice ?? ''),
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiPatch(`/projects/${project.id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', project.id] });
      setEditing(false);
      toast.success('Progetto aggiornato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleSave = () => {
    updateMutation.mutate({
      title: form.title,
      description: form.description || undefined,
      deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
      startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
      ...(isExternal && {
        deliveryDeadline: form.deliveryDeadline ? new Date(form.deliveryDeadline).toISOString() : null,
        photosToEditCount: form.photosToEditCount ? parseInt(form.photosToEditCount) : null,
        photosDeliveredCount: form.photosDeliveredCount ? parseInt(form.photosDeliveredCount) : null,
        editingStyleNotes: form.editingStyleNotes || null,
        deliveryMethod: form.deliveryMethod || null,
        externalSalePrice: form.externalSalePrice ? parseFloat(form.externalSalePrice) : null,
      }),
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div className={`project-overview-grid${isExternal ? ' external' : ''}`}>
        {/* Base info */}
        <SectionCard title="Informazioni" action={
          <Button size="sm" variant="ghost" icon={<Edit2 size={14} />} onClick={() => setEditing(!editing)}>
            {editing ? 'Annulla' : 'Modifica'}
          </Button>
        }>
          {editing ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <Input label="Titolo" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              <Input label="Descrizione" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <Input label="Data inizio" type="date" value={form.startDate} onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))} />
                <Input label="Scadenza" type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} />
              </div>
              <Button onClick={handleSave} loading={updateMutation.isPending} size="sm">Salva</Button>
            </div>
          ) : (
            <div>
              <InfoRow label="Tipo" value={TYPE_LABELS[project.type]} />
              {project.description && <InfoRow label="Descrizione" value={project.description} />}
              {project.startDate && <InfoRow label="Inizio" value={formatDate(project.startDate)} />}
              {project.deadline && <InfoRow label="Scadenza" value={formatDate(project.deadline)} />}
              {project.completedAt && <InfoRow label="Completato il" value={formatDate(project.completedAt)} />}
              {project.client && (
                <InfoRow label="Cliente" value={
                  <button onClick={() => navigate(`/clients`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontSize: 'var(--text-sm)', padding: 0, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {project.client.firstName} {project.client.lastName} <ChevronRight size={12} />
                  </button>
                } />
              )}
              {project.event && (
                <InfoRow label="Evento collegato" value={
                  <button onClick={() => navigate(`/events/${project.event!.id}`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontSize: 'var(--text-sm)', padding: 0, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {project.event.title} <ChevronRight size={12} />
                  </button>
                } />
              )}
              {project.externalPhotographer && (
                <InfoRow label="Esterno" value={
                  <button onClick={() => navigate(`/external-photographers`)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', fontSize: 'var(--text-sm)', padding: 0, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    {project.externalPhotographer.studioName ?? `${project.externalPhotographer.firstName} ${project.externalPhotographer.lastName}`} <ChevronRight size={12} />
                  </button>
                } />
              )}
            </div>
          )}
        </SectionCard>

        {/* Notes Section — Markdown editor */}
        <NotesSection
          notes={project.notes}
          saving={updateMutation.isPending}
          onSave={async (newNotes) => {
            await apiPatch(`/projects/${project.id}`, { notes: newNotes || null });
            qc.invalidateQueries({ queryKey: ['project', project.id] });
          }}
        />

        {/* External job specific */}
        {isExternal && (
          <SectionCard title="Lavoro esterno">
            {editing ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <Input label="Prezzo vendita (€)" type="number" value={form.externalSalePrice} onChange={e => setForm(f => ({ ...f, externalSalePrice: e.target.value }))} />
                <Input label="Deadline consegna" type="date" value={form.deliveryDeadline} onChange={e => setForm(f => ({ ...f, deliveryDeadline: e.target.value }))} />
                <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <Input label="Foto da editare" type="number" value={form.photosToEditCount} onChange={e => setForm(f => ({ ...f, photosToEditCount: e.target.value }))} />
                  <Input label="Foto consegnate" type="number" value={form.photosDeliveredCount} onChange={e => setForm(f => ({ ...f, photosDeliveredCount: e.target.value }))} />
                </div>
                <Input label="Metodo consegna" value={form.deliveryMethod} onChange={e => setForm(f => ({ ...f, deliveryMethod: e.target.value }))} />
                <Input label="Note stile editing" value={form.editingStyleNotes} onChange={e => setForm(f => ({ ...f, editingStyleNotes: e.target.value }))} />
              </div>
            ) : (
              <div>
                {project.externalJobType && <InfoRow label="Tipo lavorazione" value={EXT_JOB_LABELS[project.externalJobType]} />}
                {project.shootingDate && <InfoRow label="Data shooting" value={formatDate(project.shootingDate)} />}
                {project.deliveryDeadline && <InfoRow label="Deadline consegna" value={formatDate(project.deliveryDeadline)} />}
                {project.photosToEditCount != null && (
                  <InfoRow label="Avanzamento" value={
                    <span>
                      {project.photosDeliveredCount ?? 0}/{project.photosToEditCount} foto
                      {project.photosToEditCount > 0 && (
                        <span style={{ marginLeft: '0.5rem', fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
                          ({Math.round(((project.photosDeliveredCount ?? 0) / project.photosToEditCount) * 100)}%)
                        </span>
                      )}
                    </span>
                  } />
                )}
                {project.sourceMaterialReceivedAt && <InfoRow label="Materiale ricevuto" value={formatDate(project.sourceMaterialReceivedAt)} />}
                {project.deliveredToExternalAt && <InfoRow label="Consegnato il" value={formatDate(project.deliveredToExternalAt)} />}
                {project.deliveryMethod && <InfoRow label="Metodo consegna" value={project.deliveryMethod} />}
                {project.editingStyleNotes && <InfoRow label="Note editing" value={project.editingStyleNotes} />}
                {project.externalSalePrice != null && <InfoRow label="Prezzo vendita" value={formatCurrency(project.externalSalePrice)} />}
                {project.externalPaymentStatus && <InfoRow label="Stato pagamento" value={project.externalPaymentStatus === 'INCASSATO' ? '✓ Incassato' : project.externalPaymentStatus === 'PARZIALE' ? 'Parziale' : 'Da incassare'} />}
              </div>
            )}
          </SectionCard>
        )}
      </div>

      {/* Recent activity mini */}
      {(project.activities ?? []).length > 0 && (
        <SectionCard title="Ultime attività">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {(project.activities ?? []).slice(0, 4).map(a => (
              <div key={a.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', padding: '0.5rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-primary)', marginTop: '0.4rem', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{a.message}</p>
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{formatDate(a.createdAt)}</p>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ─── Economy Tab ──────────────────────────────────────────────────────────────

function EconomyTab({ project, finance, id }: { project: Project; finance?: ProjectFinanceSummary; id: string }) {
  const qc = useQueryClient();
  const [showReport, setShowReport] = useState(false);

  type ReportShape = ProjectFinanceSummary & {
    roi: number; estimatedHours: number; hourlyRate: number;
    breakEvenRevenue: number; breakEvenPct: number; breakEvenReached: boolean;
    profitPerGuest?: number; daysUntilEvent?: number;
  };
  const { data: reportData, isFetching: reportLoading } = useQuery<ReportShape>({
    queryKey: ['project-report', id],
    queryFn: () => apiGet(`/projects/${id}/financial-report`),
    enabled: showReport,
  });

  const [showExpModal, setShowExpModal] = useState(false);
  const [showRevModal, setShowRevModal] = useState(false);
  const [deleteExpId, setDeleteExpId] = useState<string | null>(null);
  const [deleteRevId, setDeleteRevId] = useState<string | null>(null);
  const [expForm, setExpForm] = useState({ label: '', category: 'VARIE' as ExpenseCategory, amountExpected: '', amountPaid: '', supplierName: '', notes: '' });
  const [revForm, setRevForm] = useState({ label: '', amountExpected: '', amountReceived: '', dueDate: '', notes: '' });

  const expMutation = useMutation({
    mutationFn: (body: typeof expForm) => apiPost(`/projects/${id}/expenses`, {
      label: body.label, category: body.category,
      amountExpected: parseFloat(body.amountExpected) || 0,
      amountPaid: parseFloat(body.amountPaid) || 0,
      supplierName: body.supplierName || undefined, notes: body.notes || undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project', id] }); qc.invalidateQueries({ queryKey: ['project-finance', id] }); setShowExpModal(false); setExpForm({ label: '', category: 'VARIE', amountExpected: '', amountPaid: '', supplierName: '', notes: '' }); toast.success('Spesa aggiunta'); },
    onError: (e: Error) => toast.error(e.message),
  });
  const revMutation = useMutation({
    mutationFn: (body: typeof revForm) => apiPost(`/projects/${id}/revenues`, {
      label: body.label, amountExpected: parseFloat(body.amountExpected) || 0,
      amountReceived: parseFloat(body.amountReceived) || 0,
      dueDate: body.dueDate ? new Date(body.dueDate).toISOString() : undefined, notes: body.notes || undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project', id] }); qc.invalidateQueries({ queryKey: ['project-finance', id] }); setShowRevModal(false); setRevForm({ label: '', amountExpected: '', amountReceived: '', dueDate: '', notes: '' }); toast.success('Ricavo aggiunto'); },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteExpMutation = useMutation({
    mutationFn: (eid: string) => apiDelete(`/projects/${id}/expenses/${eid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project', id] }); qc.invalidateQueries({ queryKey: ['project-finance', id] }); setDeleteExpId(null); toast.success('Spesa eliminata'); },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteRevMutation = useMutation({
    mutationFn: (rid: string) => apiDelete(`/projects/${id}/revenues/${rid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project', id] }); qc.invalidateQueries({ queryKey: ['project-finance', id] }); setDeleteRevId(null); toast.success('Ricavo eliminato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const expenses = project.expenses ?? [];
  const revenues = project.revenues ?? [];

  const revenueColumns: ColumnDef<ProjectRevenue>[] = [
    { key: 'label', label: 'Voce', render: (r) => (
      <div>
        <p style={{ fontWeight: 500, color: 'var(--color-text)' }}>{r.label}</p>
        {r.dueDate && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>Scadenza: {formatDate(r.dueDate)}</p>}
      </div>
    )},
    { key: 'expected', label: 'Previsto', type: 'currency', render: (r) => <span style={{ color: 'var(--color-text-muted)' }}>{formatCurrency(r.amountExpected)}</span> },
    { key: 'received', label: 'Incassato', type: 'currency', render: (r) => <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>{formatCurrency(r.amountReceived)}</span> },
    { key: 'status', label: 'Stato', render: (r) => <RevenueStatusBadge status={r.status} /> },
    { key: 'actions', label: '', render: (r) => (
      <button onClick={(e) => { e.stopPropagation(); setDeleteRevId(r.id); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.25rem', borderRadius: 'var(--radius-interactive)', display: 'flex' }}
        onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-error)')}
        onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-text-faint)')}>
        <Trash2 size={14} />
      </button>
    )},
  ];

  const expenseColumns: ColumnDef<ProjectExpense>[] = [
    { key: 'label', label: 'Voce', render: (e) => (
      <div>
        <p style={{ fontWeight: 500, color: 'var(--color-text)' }}>{e.label}</p>
        {e.supplierName && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{e.supplierName}</p>}
      </div>
    )},
    { key: 'category', label: 'Categoria', render: (e) => <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{EXPENSE_CAT_LABELS[e.category]}</span> },
    { key: 'expected', label: 'Previsto', type: 'currency', render: (e) => <span style={{ color: 'var(--color-text-muted)' }}>{formatCurrency(e.amountExpected)}</span> },
    { key: 'paid', label: 'Pagato', type: 'currency', render: (e) => <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>{formatCurrency(e.amountPaid)}</span> },
    { key: 'status', label: 'Stato', render: (e) => <ExpenseStatusBadge status={e.status} /> },
    { key: 'actions', label: '', render: (e) => (
      <button onClick={(ev) => { ev.stopPropagation(); setDeleteExpId(e.id); }}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.25rem', borderRadius: 'var(--radius-interactive)', display: 'flex' }}
        onMouseEnter={ev => (ev.currentTarget.style.color = 'var(--color-error)')}
        onMouseLeave={ev => (ev.currentTarget.style.color = 'var(--color-text-faint)')}>
        <Trash2 size={14} />
      </button>
    )},
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Finance summary KPIs */}
      {finance && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {/* KPI row + report button */}
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', flex: 1 }}>
              <KpiMini label="Ricavi previsti" value={formatCurrency(finance.totalExpectedRevenue)} />
              <KpiMini label="Ricavi incassati" value={formatCurrency(finance.totalReceivedRevenue)} color="var(--color-success)" />
              <KpiMini label="Costi previsti" value={formatCurrency(finance.totalExpectedExpenses + finance.totalCollabFeeExpected)} />
              <KpiMini label="Costi effettivi" value={formatCurrency(finance.totalPaidExpenses + finance.totalCollabFeePaid)} />
              <KpiMini label="Margine previsto" value={formatCurrency(finance.marginExpected)} color={finance.marginExpected >= 0 ? 'var(--color-success)' : 'var(--color-error)'} />
              <KpiMini label="Da incassare" value={formatCurrency(finance.toReceive)} color="var(--color-warning)" />
              <KpiMini label="Da pagare" value={formatCurrency(finance.toPay)} color="var(--color-gold)" />
            </div>
            <Button
              variant="ghost"
              size="sm"
              icon={<BarChart3 size={14} />}
              onClick={() => setShowReport(true)}
              style={{ alignSelf: 'center' }}
            >
              Report
            </Button>
          </div>

          <FinancialReportModal open={showReport} onClose={() => setShowReport(false)} data={reportData ?? null} loading={reportLoading} />
        </div>
      )}

      {/* Revenues */}
      <SectionCard title={`Ricavi (${revenues.length})`} action={
        <Button size="sm" variant="secondary" icon={<Plus size={14} />} onClick={() => setShowRevModal(true)}>Aggiungi</Button>
      }>
        <DataTable
          columns={revenueColumns}
          data={revenues}
          gridColumns="2fr 1fr 1fr 1fr 40px"
          rowKey={(r) => r.id}
          emptyState={<div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-faint)' }}>Nessun ricavo registrato</div>}
        />
      </SectionCard>

      {/* Expenses */}
      <SectionCard title={`Spese (${expenses.length})`} action={
        <Button size="sm" variant="secondary" icon={<Plus size={14} />} onClick={() => setShowExpModal(true)}>Aggiungi</Button>
      }>
        <DataTable
          columns={expenseColumns}
          data={expenses}
          gridColumns="2fr 1fr 1fr 1fr 1fr 40px"
          rowKey={(e) => e.id}
          emptyState={<div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-faint)' }}>Nessuna spesa registrata</div>}
        />
      </SectionCard>

      {/* Modals */}
      <Modal open={showRevModal} onClose={() => setShowRevModal(false)} title="Aggiungi ricavo" width={440}
        footer={<><Button variant="ghost" onClick={() => setShowRevModal(false)}>Annulla</Button><Button onClick={() => revMutation.mutate(revForm)} loading={revMutation.isPending} disabled={!revForm.label || !revForm.amountExpected}>Aggiungi</Button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <Input label="Voce *" value={revForm.label} onChange={e => setRevForm(f => ({ ...f, label: e.target.value }))} />
          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Importo previsto (€) *" type="number" value={revForm.amountExpected} onChange={e => setRevForm(f => ({ ...f, amountExpected: e.target.value }))} />
            <Input label="Già incassato (€)" type="number" value={revForm.amountReceived} onChange={e => setRevForm(f => ({ ...f, amountReceived: e.target.value }))} />
          </div>
          <Input label="Scadenza" type="date" value={revForm.dueDate} onChange={e => setRevForm(f => ({ ...f, dueDate: e.target.value }))} />
          <Input label="Note" value={revForm.notes} onChange={e => setRevForm(f => ({ ...f, notes: e.target.value }))} />
        </div>
      </Modal>

      <Modal open={showExpModal} onClose={() => setShowExpModal(false)} title="Aggiungi spesa" width={440}
        footer={<><Button variant="ghost" onClick={() => setShowExpModal(false)}>Annulla</Button><Button onClick={() => expMutation.mutate(expForm)} loading={expMutation.isPending} disabled={!expForm.label || !expForm.amountExpected}>Aggiungi</Button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <Input label="Voce *" value={expForm.label} onChange={e => setExpForm(f => ({ ...f, label: e.target.value }))} />
          <Select label="Categoria *" value={expForm.category} onChange={e => setExpForm(f => ({ ...f, category: e.target.value as ExpenseCategory }))}
            options={EXPENSE_CATEGORIES.map(c => ({ value: c, label: EXPENSE_CAT_LABELS[c] }))} />
          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Importo previsto (€) *" type="number" value={expForm.amountExpected} onChange={e => setExpForm(f => ({ ...f, amountExpected: e.target.value }))} />
            <Input label="Già pagato (€)" type="number" value={expForm.amountPaid} onChange={e => setExpForm(f => ({ ...f, amountPaid: e.target.value }))} />
          </div>
          <Input label="Fornitore" value={expForm.supplierName} onChange={e => setExpForm(f => ({ ...f, supplierName: e.target.value }))} />
          <Input label="Note" value={expForm.notes} onChange={e => setExpForm(f => ({ ...f, notes: e.target.value }))} />
        </div>
      </Modal>

      <ConfirmDialog open={!!deleteRevId} onClose={() => setDeleteRevId(null)} onConfirm={() => deleteRevId && deleteRevMutation.mutate(deleteRevId)}
        title="Elimina ricavo" description="Il ricavo verrà eliminato definitivamente." confirmLabel="Elimina" loading={deleteRevMutation.isPending} />
      <ConfirmDialog open={!!deleteExpId} onClose={() => setDeleteExpId(null)} onConfirm={() => deleteExpId && deleteExpMutation.mutate(deleteExpId)}
        title="Elimina spesa" description="La spesa verrà eliminata definitivamente." confirmLabel="Elimina" loading={deleteExpMutation.isPending} />
    </div>
  );
}

// ─── Collaborators Tab ────────────────────────────────────────────────────────

function CollaboratorsTab({ project, id }: { project: Project; id: string }) {
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [payingId, setPayingId] = useState<string | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState({ collaboratorId: '', role: 'EDITOR' as ProjectCollaboratorRole, feeExpected: '', assignedPhotosCount: '', workloadDescription: '' });

  const { data: collaborators } = useQuery<Collaborator[]>({
    queryKey: ['collaborators-list'],
    queryFn: () => apiGet('/collaborators?limit=100&active=true'),
    enabled: showModal,
  });

  const addMutation = useMutation({
    mutationFn: (body: typeof form) => apiPost(`/projects/${id}/collaborators`, {
      collaboratorId: body.collaboratorId, role: body.role,
      feeExpected: parseFloat(body.feeExpected) || 0,
      assignedPhotosCount: body.assignedPhotosCount ? parseInt(body.assignedPhotosCount) : undefined,
      workloadDescription: body.workloadDescription || undefined,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project', id] }); setShowModal(false); setForm({ collaboratorId: '', role: 'EDITOR', feeExpected: '', assignedPhotosCount: '', workloadDescription: '' }); toast.success('Collaboratore assegnato'); },
    onError: (e: Error) => toast.error(e.message),
  });
  const payMutation = useMutation({
    mutationFn: ({ assignmentId, amount }: { assignmentId: string; amount: number }) =>
      apiPatch(`/projects/${id}/collaborators/${assignmentId}/payment`, { feePaid: amount }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project', id] }); setPayingId(null); setPayAmount(''); toast.success('Pagamento registrato'); },
    onError: (e: Error) => toast.error(e.message),
  });
  const deleteMutation = useMutation({
    mutationFn: (aid: string) => apiDelete(`/projects/${id}/collaborators/${aid}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['project', id] }); setDeleteId(null); toast.success('Assegnazione rimossa'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const assignments = project.collaborators ?? [];
  const totalFeeExpected = assignments.reduce((s, a) => s + Number(a.feeExpected), 0);
  const totalFeePaid = assignments.reduce((s, a) => s + Number(a.feePaid), 0);

  const collaboratorColumns: ColumnDef<ProjectCollaboratorAssignment>[] = [
    { key: 'collaborator', label: 'Collaboratore', render: (a) => (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <UserAvatar avatarUrl={a.collaborator.avatarUrl} name={a.collaborator.name} size={28} />
          <p style={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{a.collaborator.name}</p>
        </div>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{COLLAB_ROLE_LABELS[a.role]}{a.assignedPhotosCount ? ` · ${a.assignedPhotosCount} foto` : ''}</p>
        {a.workloadDescription && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.125rem' }}>{a.workloadDescription}</p>}
      </div>
    )},
    { key: 'fee', label: 'Compenso', type: 'currency', render: (a) => (
      <div>
        <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{formatCurrency(a.feeExpected)}</p>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-success)' }}>Pagato: {formatCurrency(a.feePaid)}</p>
      </div>
    )},
    { key: 'remaining', label: 'Residuo', render: (a) => (
      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gold)', fontWeight: 600 }}>Residuo: {formatCurrency(Number(a.feeExpected) - Number(a.feePaid))}</p>
    )},
    { key: 'status', label: 'Stato', render: (a) => <PaymentStatusBadgeSmall status={a.paymentStatus} /> },
    { key: 'actions', label: '', render: (a) => (
      <div style={{ display: 'flex', gap: '0.375rem' }}>
        {a.paymentStatus !== 'PAGATO' && (
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setPayingId(a.id); setPayAmount(String(Number(a.feeExpected) - Number(a.feePaid))); }}>Paga</Button>
        )}
        <button onClick={(e) => { e.stopPropagation(); setDeleteId(a.id); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.25rem', display: 'flex', borderRadius: 'var(--radius-interactive)' }}
          onMouseEnter={e => (e.currentTarget.style.color = 'var(--color-error)')} onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-text-faint)')}>
          <Trash2 size={14} />
        </button>
      </div>
    )},
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {assignments.length > 0 && (
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <KpiMini label="Totale compensi" value={formatCurrency(totalFeeExpected)} />
          <KpiMini label="Pagato" value={formatCurrency(totalFeePaid)} color="var(--color-success)" />
          <KpiMini label="Da pagare" value={formatCurrency(totalFeeExpected - totalFeePaid)} color="var(--color-gold)" />
        </div>
      )}

      <SectionCard title={`Collaboratori assegnati (${assignments.length})`} action={
        <Button size="sm" variant="secondary" icon={<Plus size={14} />} onClick={() => setShowModal(true)}>Assegna</Button>
      }>
        <DataTable
          columns={collaboratorColumns}
          data={assignments}
          gridColumns="2fr 1fr 1fr 1fr auto"
          rowKey={(a) => a.id}
          emptyState={<div style={{ padding: '2rem', textAlign: 'center', color: 'var(--color-text-faint)' }}>Nessun collaboratore assegnato</div>}
        />
      </SectionCard>

      {/* Assign modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Assegna collaboratore" width={440}
        footer={<><Button variant="ghost" onClick={() => setShowModal(false)}>Annulla</Button><Button onClick={() => addMutation.mutate(form)} loading={addMutation.isPending} disabled={!form.collaboratorId || !form.feeExpected}>Assegna</Button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <Select label="Collaboratore *" value={form.collaboratorId} onChange={e => setForm(f => ({ ...f, collaboratorId: e.target.value }))}
            placeholder="Seleziona…"
            options={(collaborators ?? []).map(c => ({ value: c.id, label: `${c.name} — ${c.role}` }))} />
          <Select label="Ruolo nel progetto *" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value as ProjectCollaboratorRole }))}
            options={(Object.keys(COLLAB_ROLE_LABELS) as ProjectCollaboratorRole[]).map(r => ({ value: r, label: COLLAB_ROLE_LABELS[r] }))} />
          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Compenso (€) *" type="number" value={form.feeExpected} onChange={e => setForm(f => ({ ...f, feeExpected: e.target.value }))} />
            <Input label="Foto assegnate" type="number" value={form.assignedPhotosCount} onChange={e => setForm(f => ({ ...f, assignedPhotosCount: e.target.value }))} />
          </div>
          <Input label="Descrizione carico" value={form.workloadDescription} onChange={e => setForm(f => ({ ...f, workloadDescription: e.target.value }))} />
        </div>
      </Modal>

      {/* Pay modal */}
      <Modal open={!!payingId} onClose={() => { setPayingId(null); setPayAmount(''); }} title="Registra pagamento" width={360}
        footer={<><Button variant="ghost" onClick={() => { setPayingId(null); setPayAmount(''); }}>Annulla</Button><Button onClick={() => payingId && payMutation.mutate({ assignmentId: payingId, amount: parseFloat(payAmount) || 0 })} loading={payMutation.isPending}>Registra</Button></>}>
        <Input label="Importo pagato (€)" type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)} />
      </Modal>

      <ConfirmDialog open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        title="Rimuovi assegnazione" description="Il collaboratore verrà rimosso dal progetto." confirmLabel="Rimuovi" loading={deleteMutation.isPending} />
    </div>
  );
}

// ─── Activity Tab ─────────────────────────────────────────────────────────────

function ActivityTab({ activities, id }: { activities: ProjectActivity[]; id: string }) {
  const ACTIVITY_ICONS: Record<string, React.ReactNode> = {
    CREATED: <CheckCircle size={14} />, STATUS_CHANGED: <TrendingUp size={14} />,
    COLLABORATOR_ASSIGNED: <Camera size={14} />, PAYMENT_RECORDED: <DollarSign size={14} />,
    DELIVERY_COMPLETED: <ExternalLink size={14} />, MATERIAL_RECEIVED: <AlertCircle size={14} />,
    NOTE_ADDED: <Edit2 size={14} />, CLOSED: <CheckCircle size={14} />,
  };

  return (
    <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '1.25rem' }}>
      <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600, marginBottom: '1.25rem' }}>Timeline attività</h3>
      {activities.length === 0 ? (
        <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: '1rem' }}>Nessuna attività registrata</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {activities.map((a, i) => (
            <div key={a.id} style={{ display: 'flex', gap: '0.875rem', paddingBottom: i < activities.length - 1 ? '1rem' : 0, position: 'relative' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--color-primary-highlight)', border: '1px solid rgba(255,255,255,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-primary)', flexShrink: 0 }}>
                  {ACTIVITY_ICONS[a.type] ?? <Clock size={14} />}
                </div>
                {i < activities.length - 1 && <div style={{ width: 1, flex: 1, background: 'rgba(255,255,255,0.10)', marginTop: '0.25rem', minHeight: 16 }} />}
              </div>
              <div style={{ flex: 1, paddingTop: '0.25rem' }}>
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', fontWeight: 500 }}>{a.message}</p>
                <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.125rem' }}>{formatDate(a.createdAt)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Mini badge helpers ───────────────────────────────────────────────────────

function ExpenseStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = { DA_PAGARE: 'var(--color-warning)', PARZIALE: 'var(--color-primary)', PAGATO: 'var(--color-success)', ANNULLATO: 'var(--color-text-faint)' };
  const labels: Record<string, string> = { DA_PAGARE: 'Da pagare', PARZIALE: 'Parziale', PAGATO: 'Pagato', ANNULLATO: 'Annullato' };
  const c = colors[status] ?? 'var(--color-text-muted)';
  return <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: c }}>{labels[status] ?? status}</span>;
}

function RevenueStatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = { DA_INCASSARE: 'var(--color-warning)', PARZIALE: 'var(--color-primary)', INCASSATO: 'var(--color-success)', ANNULLATO: 'var(--color-text-faint)' };
  const labels: Record<string, string> = { DA_INCASSARE: 'Da incassare', PARZIALE: 'Parziale', INCASSATO: 'Incassato', ANNULLATO: 'Annullato' };
  const c = colors[status] ?? 'var(--color-text-muted)';
  return <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: c }}>{labels[status] ?? status}</span>;
}

const PAYMENT_SOLID: Record<string, string> = { DA_PAGARE: '#F59E0B', PARZIALE: '#3E91FF', PAGATO: '#22C55E' };

function PaymentStatusBadgeSmall({ status }: { status: string }) {
  const labels: Record<string, string> = { DA_PAGARE: 'Da pagare', PARZIALE: 'Parziale', PAGATO: 'Pagato' };
  return <span style={{ fontSize: 12, fontWeight: 600, color: '#FFFFFF', background: PAYMENT_SOLID[status] ?? '#6B7280', padding: '4px 10px', borderRadius: 'var(--radius-interactive)', display: 'inline-flex', alignItems: 'center' }}>{labels[status] ?? status}</span>;
}
